import { useState } from "react";
import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";
import { useContentExplorer } from "@/hooks/useContentExplorer";
import { getAllGenres, getAllRegions, getAllYears } from "@/utils/content";
import FilterSidebar from "@/components/feature/FilterSidebar";
import SortBar from "@/components/feature/SortBar";
import ContentListItem from "@/components/feature/ContentListItem";
import ContentCard from "@/components/feature/ContentCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

interface Props {
  baseItems: ContentItem[];
  keyword: string;
  hotItems: ContentItem[];
}

export default function SearchExplorer({ baseItems, keyword, hotItems }: Props) {
  const explorer = useContentExplorer(baseItems, 6);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filterProps = {
    regionOptions: getAllRegions(),
    yearOptions: getAllYears().map(String),
    genreOptions: getAllGenres(),
    selected: explorer.selected,
    onToggle: (type: "region" | "year" | "genre", value: string) => {
      if (type === "region") explorer.toggleRegion(value);
      if (type === "year") explorer.toggleYear(value);
      if (type === "genre") explorer.toggleGenre(value);
    },
    onClear: explorer.clearFilters,
  };

  if (baseItems.length === 0) {
    return (
      <div>
        <EmptyState
          title={`没有找到与「${keyword}」相关的资源`}
          description="换个关键词试试，或浏览下面的热门推荐，发现更多国语对白影音作品。"
          actionLabel="浏览热门资源"
          onAction={explorer.clearFilters}
        />
        <section className="mt-10">
          <h2 className="text-base md:text-lg font-bold text-foreground-950 font-heading flex items-center gap-2 mb-5">
            <span className="w-1 h-5 rounded-full bg-accent-500" />
            热门推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {hotItems.slice(0, 10).map((item, i) => (
              <ContentCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
      <aside className="hidden lg:block lg:w-64 shrink-0">
        <div className="sticky top-24">
          <FilterSidebar {...filterProps} />
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <SortBar
          sort={explorer.sort}
          onChange={explorer.setSort}
          total={explorer.total}
          onOpenFilter={() => setDrawerOpen(true)}
        />

        {explorer.paged.length > 0 ? (
          <>
            <div className="flex flex-col gap-3.5">
              {explorer.paged.map((item, i) => (
                <ContentListItem key={item.id} item={item} index={i} />
              ))}
            </div>
            <Pagination current={explorer.page} total={explorer.totalPages} onChange={explorer.setPage} />
          </>
        ) : (
          <EmptyState
            title="当前筛选下没有匹配结果"
            description="试着减少筛选条件，或清空筛选查看全部搜索结果。"
            actionLabel="清空筛选"
            onAction={explorer.clearFilters}
          />
        )}
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="relative bg-background-50 rounded-t-2xl max-h-[80vh] overflow-y-auto p-4 pb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold text-foreground-900 font-heading">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-600" />
              </button>
            </div>
            <FilterSidebar {...filterProps} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-600 py-3 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
            >
              查看 {explorer.total} 部结果
            </button>
          </div>
        </div>
      )}

      <div className="hidden lg:block w-64 shrink-0">
        <h2 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
          <i className="ri-fire-line text-accent-500" />
          热门资源
        </h2>
        <div className="flex flex-col gap-3">
          {hotItems.slice(0, 4).map((item) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-12 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 国语对白影音资源`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-900 line-clamp-2">{item.title}</p>
                <p className="mt-1 text-xs text-foreground-500">{item.region} · {item.year}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}